import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLayerGroup, faTimes } from '@fortawesome/free-solid-svg-icons'
import { cn } from '../shared/utils/cn'
import { MAX_COMPARE_DEVICES } from '../common/constants'

interface CompareModeBannerProps {
  selectedCount: number
  variant?: 'single' | 'mixed'
  // When provided, renders an always-visible Exit control. This is the primary
  // way to leave compare mode on mobile, where the device drawer (and its
  // Compare switch) is closed while the comparison grid is on screen.
  onExit?: () => void
  className?: string
}

export function CompareModeBanner({
  selectedCount,
  variant = 'single',
  onExit,
  className = '',
}: CompareModeBannerProps) {
  // Mixed mode can chart a single device; single mode needs two.
  const minDevices = variant === 'mixed' ? 1 : 2
  const hint = variant === 'mixed' ? '(pick sensors below)' : '(select sensor type below)'
  return (
    <div
      className={cn(
        'flex items-center justify-between px-4 py-2',
        'bg-purple-50 border-b border-purple-200',
        'animate-in fade-in slide-in-from-top-1 duration-200',
        className
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100">
          <FontAwesomeIcon icon={faLayerGroup} className="text-purple-600 text-sm" />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium text-purple-800">
            Compare Mode
          </span>
          <span className="text-xs text-purple-600">
            {selectedCount === 0 ? (
              'Select devices to compare'
            ) : (
              <>
                <span className="font-semibold">{selectedCount}</span>
                <span className="text-purple-500">/{MAX_COMPARE_DEVICES}</span>
                {' devices selected'}
                {selectedCount >= minDevices && (
                  <span className="ml-2 text-purple-400">
                    {hint}
                  </span>
                )}
              </>
            )}
          </span>
        </div>
      </div>
      {onExit && (
        <button
          type="button"
          onClick={onExit}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-purple-700 hover:bg-purple-100 transition-colors flex-shrink-0"
          aria-label="Exit compare mode"
        >
          <FontAwesomeIcon icon={faTimes} className="text-xs" />
          Exit
        </button>
      )}
    </div>
  )
}
